// src/components/GuideModal.jsx
import React, { useState, useRef, useEffect } from "react";
import "./css/GuideModal.css";
import { db, auth } from "../firebase";
import {
    doc, setDoc, serverTimestamp, writeBatch, collection,
    getDocs,
    updateDoc
} from "firebase/firestore";
import { normalizeSlug } from "../utils/slug";

export const GUIDE_JSON = {
    title: "Bagno Vignoni",
    slug: "bagno-vignoni",
    tags: [
        "bagno vignoni travel guide",
        "visit bagno vignoni italy",
        "things to do in bagno vignoni",
        "val d orcia thermal springs",
        "tuscany spa village",
        "italy hot springs",
        "bagno vignoni thermal square",
        "tuscany countryside",
        "northern italy travel",
        "bagno vignoni italy attractions"
    ],

    searchableText:
        "bagno vignoni italy travel guide thermal springs val d orcia tuscany spa village parco dei mulini europe trip things to do in bagno vignoni",

    country: {
        type: "country",
        name: "Italy",
        code: "IT",
        continent: "Europe",
        currency: "EUR",
        language: "Italian",
        flag: "🇮🇹"
    },

    city: {
        type: "city",
        name: "Bagno Vignoni",
        state: "Tuscany",
        population: "50",
        timezone: "CET (UTC+1)"
    },

    guide: {
        id: "bagno-vignoni",
        title: "Bagno Vignoni Travel Guide",
        subtitle: "Tiny Tuscan spa village with historic thermal waters.",
        pill: "Village Travel Guide",
        heroClass: "bagno-vignoni",
        heroImage: ""
    },

    quickFacts: {
        region: "Val d’Orcia, Tuscany",
        bestMonths: "Apr–Jun & Sep–Oct",
        idealDuration: "Half-day",
        dailyBudgetRange: "€60–160",
        walkability: "High (very small village)"
    },

    sections: {
        whyVisit: {
            title: "Why visit Bagno Vignoni?",
            description:
                "Bagno Vignoni is one of Tuscany’s most unique villages, centered around a large thermal water pool instead of a traditional piazza. It offers peaceful countryside charm and natural hot springs."
        },

        bestTimeToVisit: {
            title: "Best time to visit",
            seasons: [
                { name: "Spring", emoji: "🌸", months: "Apr–Jun", note: "Green countryside & mild weather" },
                { name: "Summer", emoji: "☀️", months: "Jul–Aug", note: "Warm but scenic" },
                { name: "Autumn", emoji: "🍂", months: "Sep–Oct", note: "Quiet & pleasant temperatures" },
                { name: "Winter", emoji: "❄️", months: "Nov–Mar", note: "Thermal waters feel magical" }
            ]
        },

        neighborhoods: {
            title: "Popular areas",
            areas: [
                { name: "Thermal Square", emoji: "♨️", highlights: "Historic hot spring pool" },
                { name: "Parco dei Mulini", emoji: "🌿", highlights: "Thermal waterfalls & ruins" },
                { name: "Countryside Trails", emoji: "🌄", highlights: "Scenic Val d’Orcia views" }
            ]
        },

        budget: {
            title: "Estimated daily budget",
            tiers: [
                { type: "Backpacker", range: "€50–90" },
                { type: "Mid-range", range: "€100–160", highlight: true },
                { type: "Luxury", range: "€250+" }
            ]
        },

        gettingAround: {
            title: "Getting around Bagno Vignoni",
            description:
                "The village is extremely small and fully walkable. A car is recommended to reach nearby towns and countryside."
        },

        cta: {
            title: "Planning a Bagno Vignoni trip?",
            subtitle: "Save this guide or add it to your Val d’Orcia itinerary.",
            actions: ["Add Trip", "Save Guide"]
        }
    },

    travelGuide: {
        wifiAndInternet: {
            title: "Wi-Fi & Internet",
            tips: [
                "Wi-Fi available in hotels",
                "Good mobile coverage",
                "Reliable signal in village center"
            ]
        },

        simAndEsim: {
            title: "SIM & eSIM",
            options: [
                "Tourist SIMs available in Florence",
                "Affordable prepaid plans",
                "Top providers: TIM, Vodafone, WindTre"
            ],
            tip: "Reliable coverage throughout area"
        },

        moneyAndPayments: {
            title: "Money & Payments",
            currency: "Euro (€)",
            tips: [
                "Cards accepted in restaurants",
                "Carry cash for small purchases",
                "Limited ATMs nearby"
            ]
        },

        transportAndCabs: {
            title: "Transport & Cabs",
            tips: [
                "Best reached by car",
                "Limited public transport",
                "Combine visit with Pienza"
            ]
        },

        essentialApps: {
            title: "Best Apps to Install",
            categories: [
                { category: "Transport", apps: ["Google Maps"] },
                { category: "Travel", apps: ["Tripadvisor", "Google Translate"] },
                { category: "Wellness", apps: ["GetYourGuide"] },
                { category: "Payments", apps: ["Wise", "Revolut"] }
            ]
        },

        safetyAndTips: {
            title: "Safety & Local Tips",
            tips: [
                "Do not enter central thermal pool",
                "Visit Parco dei Mulini for natural springs",
                "Wear proper footwear on trails",
                "Visit during sunset for best photos"
            ]
        }
    },

    gallery: [],

    placesToVisit: [
        { label: "Bagno Vignoni Thermal Square", placeId: "bagno-vignoni-thermal-square" },
        { label: "Parco dei Mulini", placeId: "parco-dei-mulini" },
        { label: "Natural Thermal Springs Area", placeId: "bagno-vignoni-natural-springs" }
    ]
};

const PLACES_FOR_UPLOAD = [

    {
        id: "herculaneum",
        slug: "herculaneum",
        name: "Herculaneum (Ercolano Archaeological Site)",
        city: "naples",
        country: "italy",
        guidesId: "naples",
        type: "archaeological-site",
        shortDescription:
            "Exceptionally preserved ancient Roman city buried by the eruption of Mount Vesuvius in 79 AD.",
        recommendedTime: "2–3 hours",
        bestTimeToVisit: "Morning",
        entryFee: "Paid",
        location: { lat: 40.8060, lng: 14.3480 },
        images: [],
        tips: [
            "Buy tickets online in advance",
            "Less crowded than Pompeii",
            "Wear comfortable shoes",
            "Bring water in summer"
        ],
        tags: [
            "herculaneum",
            "ercolano ruins",
            "ancient rome italy",
            "vesuvius eruption site"
        ],
        searchableText:
            "herculaneum ercolano archaeological site italy ancient roman city vesuvius eruption ruins near naples"
    }

];


export const GUIDE_METADATA_TO_UPDATE = {
    "thailand": {
        tags: [
            "thailand",
            "thailand travel guide",
            "thailand trip planner",
            "thailand itinerary",
            "2 week thailand itinerary",
            "10 day thailand itinerary",
            "best places in thailand",
            "things to do in thailand",
            "bangkok thailand",
            "phuket thailand",
            "chiang mai thailand",
            "krabi thailand",
            "koh samui",
            "pattaya thailand",
            "chiang rai thailand",
            "ayutthaya thailand",
            "thai islands",
            "island hopping thailand",
            "thailand beaches",
            "thailand temples",
            "thai street food",
            "backpacking thailand",
            "budget travel thailand",
            "luxury travel thailand",
            "honeymoon thailand",
            "thailand nightlife",
            "full moon party thailand",
            "southeast asia travel",
            "first time thailand",
            "digital nomad thailand",
            "thailand visa guide",
            "best time to visit thailand",
            "thailand weather guide",
            "thailand travel tips",
            "thailand food guide",
            "thai culture",
            "thailand family travel",
            "solo travel thailand",
            "thailand resorts",
            "thailand islands guide"
        ],

        searchableText:
            "thailand travel guide thailand itinerary 2 week thailand itinerary best places in thailand bangkok phuket chiang mai krabi koh samui pattaya chiang rai ayutthaya thailand beaches temples island hopping thai street food backpacking thailand honeymoon thailand digital nomad thailand southeast asia travel best time to visit thailand thailand visa travel tips family solo luxury budget travel"
    }

};

const generatePlaceMetadata = (place) => {
    const name = place.name?.toLowerCase() || "";
    const city = place.city?.toLowerCase() || "";
    const country = place.country?.toLowerCase() || "";
    const type = place.type?.toLowerCase() || "";
    const description = place.shortDescription?.toLowerCase() || "";

    const baseTags = [
        name,
        `${name} ${city}`,
        `${name} ${country}`,
        `things to do in ${name}`,
        `${type} in ${city}`,
        `${city} attractions`,
        `${country} travel`,
    ];

    const tags = [...new Set(baseTags.filter(Boolean))];

    const searchableText = [
        name,
        city,
        country,
        type,
        description,
        ...place.tips || []
    ]
        .join(" ")
        .toLowerCase();

    return { tags, searchableText };
};

const updateAllPlacesMetadata = async () => {
    const snapshot = await getDocs(collection(db, "places"));

    for (const docSnap of snapshot.docs) {
        const place = docSnap.data();

        const { tags, searchableText } = generatePlaceMetadata(place);

        await updateDoc(doc(db, "places", docSnap.id), {
            tags,
            searchableText,
            updatedAt: new Date()
        });

        console.log("Updated:", place.slug);
    }

    alert("All places auto-updated 🚀");
};


export default function GuideModal({ open, onClose, currentUserId }) {
    const titleRef = useRef(null);
    const [title, setTitle] = useState("");
    const [slug, setSlug] = useState("");
    const [country, setCountry] = useState("");
    const [error, setError] = useState(null);
    const [saving, setSaving] = useState(false);
    const [guideJsonInput, setGuideJsonInput] = useState("");
    const [placesJsonInput, setPlacesJsonInput] = useState("");


    useEffect(() => {
        if (open) {
            setTimeout(() => titleRef.current?.focus(), 50);
        }
    }, [open]);

    if (!open) return null;

    const resetForm = () => {
        setTitle("");
        setSlug("");
        setCountry("");
        setGuideJsonInput("");
        setPlacesJsonInput("");
        setError(null);
        setSaving(false);
    };

    const handleClose = () => {
        resetForm();
        onClose();
    };

    const handleSave = async () => {
        if (!guideJsonInput.trim() && !placesJsonInput.trim()) {
            setError("Nothing to save");
            return;
        }

        setSaving(true);
        setError(null);

        try {
            if (guideJsonInput.trim()) {
                await uploadGuide();
            }

            if (placesJsonInput.trim()) {
                await uploadPlaces();
            }

            alert("Saved successfully ✅");
            handleClose();
        } catch (err) {
            setError(err.message);
        } finally {
            setSaving(false);
        }
    };
    const uploadGuide = async () => {
        try {
            if (!auth.currentUser) {
                throw new Error("You must be logged in");
            }

            if (!guideJsonInput.trim()) {
                throw new Error("Guide JSON is empty");
            }

            setSaving(true);
            setError(null);

            const parsedJSON = JSON.parse(guideJsonInput);
            const normalizedSlug = normalizeSlug(
                slug || parsedJSON.slug || title
            );

            await setDoc(
                doc(db, "guides", normalizedSlug),
                {
                    ...parsedJSON,
                    id: normalizedSlug,
                    slug: normalizedSlug,
                    createdBy: auth.currentUser.uid,

                    // 🔥 Social Metrics (Initialize once)
                    likesCount: 0,
                    views: 0,
                    savesCount: 0,

                    createdAt: serverTimestamp(),
                    updatedAt: serverTimestamp(),
                },
                { merge: false } // important for clean creation
            );
            await setDoc(
                doc(db, "slugs", normalizedSlug),
                {
                    guideId: normalizedSlug,
                    ownerId: auth.currentUser.uid,
                    createdAt: serverTimestamp(),
                },
                { merge: false }
            );

        } catch (err) {
            console.error(err);
            setError(err.message || "Failed to upload guide");
            throw err; // important so handleSave knows it failed
        } finally {
            setSaving(false);
        }
    };

    const uploadPlaces = async () => {
        if (!auth.currentUser) {
            throw new Error("You must be logged in");
        }

        if (!placesJsonInput.trim()) {
            throw new Error("Places JSON is empty");
        }

        const places = JSON.parse(placesJsonInput);
        if (!Array.isArray(places)) {
            throw new Error("Places JSON must be an array");
        }

        const batch = writeBatch(db);

        places.forEach((place) => {
            if (!place.id || !place.name) {
                throw new Error("Each place must have id and name");
            }

            const placeSlug = normalizeSlug(place.slug || place.name);

            // 1️⃣ Save place document
            batch.set(
                doc(db, "places", place.id),
                {
                    ...place,
                    slug: placeSlug,
                    createdBy: auth.currentUser.uid,

                    likesCount: 0,
                    views: 0,
                    savesCount: 0,

                    createdAt: serverTimestamp(),
                    updatedAt: serverTimestamp(),
                },
                { merge: false }
            );

            // 2️⃣ Register slug
            batch.set(
                doc(db, "slugs", placeSlug),
                {
                    placeId: place.id, // ✅ new supported type
                    ownerId: auth.currentUser.uid,
                    createdAt: serverTimestamp(),
                },
                { merge: false }
            );
        });

        await batch.commit();
    };

    const loadGuideJSON = (json) => {
        try {
            setTitle(json.title || "");
            setSlug(json.slug || json.id || "");
            setCountry(json.country?.name || "");
            setGuideJsonInput(JSON.stringify(json, null, 2));
            setError(null);
        } catch {
            setError("Invalid Guide JSON");
        }
    };
    const loadPlacesJSON = (placesArray) => {
        if (!Array.isArray(placesArray)) {
            setError("Places JSON must be an array");
            return;
        }
        setPlacesJsonInput(JSON.stringify(placesArray, null, 2));
    };
    const fixAllPlacesCounters = async () => {
        if (!auth.currentUser) {
            alert("You must be logged in");
            return;
        }

        if (!window.confirm("This will update ALL places. Continue?")) {
            return;
        }

        try {
            setSaving(true);

            const snap = await getDocs(collection(db, "places"));

            let updatedCount = 0;

            for (const docSnap of snap.docs) {
                const data = docSnap.data();

                const needsUpdate =
                    typeof data.likesCount !== "number" ||
                    typeof data.savesCount !== "number" ||
                    typeof data.views !== "number";

                if (needsUpdate) {
                    await updateDoc(docSnap.ref, {
                        likesCount:
                            typeof data.likesCount === "number"
                                ? data.likesCount
                                : 0,
                        savesCount:
                            typeof data.savesCount === "number"
                                ? data.savesCount
                                : 0,
                        views:
                            typeof data.views === "number"
                                ? data.views
                                : 0,
                    });

                    updatedCount++;
                }
            }

            alert(`✅ Updated ${updatedCount} places`);
        } catch (err) {
            console.error(err);
            alert("Error updating places");
        } finally {
            setSaving(false);
        }
    };
    const updateGuideMetadata = async () => {
        if (!auth.currentUser) {
            alert("You must be logged in");
            return;
        }

        try {
            setSaving(true);

            const slugs = Object.keys(GUIDE_METADATA_TO_UPDATE);

            if (!slugs.length) {
                alert("No guides defined in GUIDE_METADATA_TO_UPDATE");
                return;
            }

            for (const guideSlug of slugs) {
                const metadata = GUIDE_METADATA_TO_UPDATE[guideSlug];

                await updateDoc(doc(db, "guides", guideSlug), {
                    tags: metadata.tags,
                    searchableText: metadata.searchableText.toLowerCase(),
                    updatedAt: serverTimestamp(),
                });
            }

            alert("✅ All guide metadata updated successfully");

        } catch (err) {
            console.error(err);
            alert("Error updating guide metadata");
        } finally {
            setSaving(false);
        }
    };

    return (
        <div
            className="modal-backdrop"
            onMouseDown={(e) => e.target === e.currentTarget && handleClose()}
        >
            <div className="modal guide-modal">
                <h3>Create Guide</h3>

                {/* Title */}
                <label>Title *</label>
                <input
                    ref={titleRef}
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    placeholder="France Travel Guide"
                />

                {/* Slug */}
                <label>Slug (optional)</label>
                <input
                    value={slug}
                    onChange={(e) => setSlug(e.target.value)}
                    placeholder="france"
                />

                {/* Country */}
                <label>Country</label>
                <input
                    value={country}
                    onChange={(e) => setCountry(e.target.value)}
                    placeholder="France"
                />

                {/* JSON */}
                <label>Guide JSON *</label>
                <textarea
                    rows={14}
                    className="json-preview"
                    value={guideJsonInput}
                    onChange={(e) => setGuideJsonInput(e.target.value)}
                    placeholder="{ ...full guide json... }"
                />

                <label>Places JSON</label>
                <textarea
                    rows={14}
                    className="json-preview"
                    value={placesJsonInput}
                    onChange={(e) => setPlacesJsonInput(e.target.value)}
                    placeholder="[ { ...place }, { ...place } ]"
                />

                {error && <div className="error">{error}</div>}

                <div className="controls">
                    <button
                        className="btn-primary"
                        onClick={handleSave}
                        disabled={saving}
                    >
                        {saving ? "Saving..." : "Save Guide"}
                    </button>
                    <div className="json-actions">
                        <button
                            type="button"
                            className="btn-secondary"
                            onClick={() => loadGuideJSON(GUIDE_JSON)}
                        >
                            📘 Load Guide JSON
                        </button>

                        <button
                            type="button"
                            className="btn-secondary"
                            onClick={() => loadPlacesJSON(PLACES_FOR_UPLOAD)}
                        >
                            📍 Load Places JSON
                        </button>
                    </div>
                    <button
                        type="button"
                        className="btn-secondary"
                        onClick={fixAllPlacesCounters}
                        disabled={saving}
                    >
                        🔧 Add likes,viewcount,savecount
                    </button>


                    <button
                        type="button"
                        className="btn-secondary"
                        onClick={updateGuideMetadata}
                        disabled={saving}
                    >
                        🏷 Update Tags + Search Text
                    </button>
                    <button onClick={updateAllPlacesMetadata}>
                        Auto Generate Metadata
                    </button>

                </div>
            </div>
        </div>
    );
}
